import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { useExplanationFilterLines } from '../explanationFilters'
import { fetchStaatidOversikt, UNKNOWN_ERROR } from './api'
import type { StaatidOversiktResponse, StaatidParams } from './api'
import KpiCards from './KpiCards'
import FordelingCharts from './FordelingCharts'
import TrendChart from './TrendChart'
import StaatidFilterBar, { EMPTY_LOCAL_FILTERS } from './StaatidFilterBar'
import type { StaatidLocalFilters } from './StaatidFilterBar'
import FilterSlot, { useLocalFilter } from '../FilterSlot'
import QueryExplanationBox from '../../../components/QueryExplanationBox'
import { useMaxDataDate } from '../../../hooks/useMaxDataDate'

export default function OversiktTab() {
  const { t } = useTranslation('staatid')
  const { from, to, selgerPostnummer } = useBruktbilFilters()
  const maxDataDate = useMaxDataDate()
  const filterLines = useExplanationFilterLines({ staatid: true })

  // #526 — chips write to the layout's draft; the query reads the applied
  // value, committed by the single Oppdater in the filter box.
  const [localDraft, setLocalDraft, localFilters] =
    useLocalFilter<StaatidLocalFilters>('staatid', EMPTY_LOCAL_FILTERS)
  const [oversikt, setOversikt] = useState<StaatidOversiktResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filters: StaatidParams = { from, to, selgerPostnummer, ...localFilters }
  const filterKey = `${from}|${to}|${selgerPostnummer ?? ''}|${JSON.stringify(localFilters)}`

  useLatest(alive => {
    setLoading(true)
    setError(null)
    fetchStaatidOversikt(filters)
      .then(o => { if (alive()) setOversikt(o.data) })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [filterKey])

  if (error) {
    return (
      <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
        {t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <FilterSlot>
        <StaatidFilterBar value={localDraft} onChange={setLocalDraft} />
      </FilterSlot>
      <QueryExplanationBox
        maxDataDate={maxDataDate}
        outputMode="custom"
        vehicleFilter="alle"
        periodLabel={t('oversikt.periodLabel')}
        startDate={from}
        endDate={to}
        filterLines={filterLines}
        maxDataDateLabel={t('oversikt.maxDataDateLabel')}
        extraWarnings={[
          t('oversikt.warningPeriod'),
          t('oversikt.warningFilters'),
        ]}
      />
      <KpiCards data={loading ? null : oversikt} />
      <FordelingCharts
        buckets={oversikt?.buckets ?? []}
        antallTransfers={oversikt?.antall_transfers_fordeling ?? []}
        loading={loading}
      />
      <TrendChart data={oversikt?.trend ?? []} loading={loading} maxDataDate={maxDataDate} />
    </div>
  )
}
